import { useState } from "react";
import { levelOptions, regionOptions, timeOptions } from "@/mocks/videos";

export interface FilterState {
  timeRange: string;
  level: string;
  region: string;
}

interface FilterBarProps {
  value: FilterState;
  sort: "latest" | "hot";
  resultCount: number;
  onChange: (next: FilterState) => void;
  onSortChange: (sort: "latest" | "hot") => void;
  onReset: () => void;
}

const groups = [
  { key: "timeRange" as const, label: "赛事时间段", options: timeOptions },
  { key: "level" as const, label: "比赛等级", options: levelOptions },
  { key: "region" as const, label: "地区", options: regionOptions },
];

export default function FilterBar({
  value,
  sort,
  resultCount,
  onChange,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const activeCount = [value.timeRange, value.level, value.region].filter(
    (item) => !item.startsWith("全部"),
  ).length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            className="flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-800 md:hidden"
            aria-expanded={open}
          >
            <i className="ri-filter-3-line text-base" />
            筛选
            {activeCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] text-primary-950">
                {activeCount}
              </span>
            )}
            <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
          <p className="hidden text-sm text-foreground-600 md:block">
            共 <span className="font-semibold text-foreground-950">{resultCount}</span> 条结果
          </p>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="hidden items-center gap-1 whitespace-nowrap text-xs text-primary-700 hover:text-primary-800 md:flex"
            >
              <i className="ri-close-circle-line" />
              清空筛选
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="font-label text-[11px] tracking-widest text-foreground-400">SORT</span>
          <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
            <button
              type="button"
              onClick={() => onSortChange("latest")}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                sort === "latest"
                  ? "bg-primary-500 font-medium text-background-50 dark:text-foreground-950"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              按上传时间
            </button>
            <button
              type="button"
              onClick={() => onSortChange("hot")}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                sort === "hot"
                  ? "bg-primary-500 font-medium text-background-50 dark:text-foreground-950"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              按热度
            </button>
          </div>
        </div>
      </div>

      <div
        className={`border-t border-background-200 px-4 py-4 md:block md:px-5 ${
          open ? "block" : "hidden"
        }`}
      >
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              <span className="w-20 shrink-0 text-xs font-medium text-foreground-500">{group.label}</span>
              <div className="flex flex-wrap gap-1.5">
                {group.options.map((option) => {
                  const active = value[group.key] === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => onChange({ ...value, [group.key]: option })}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                        active
                          ? "bg-primary-500 font-medium text-background-50 dark:text-foreground-950"
                          : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onReset}
          className="mt-4 flex w-full items-center justify-center gap-1 rounded-md border border-background-300 py-2 text-xs text-foreground-600 md:hidden"
        >
          <i className="ri-refresh-line" />
          重置筛选条件
        </button>
      </div>
    </div>
  );
}